import React, { useMemo, useState } from "react";
import { Drawer } from "antd";
import { ClearOutlined, CloseOutlined } from "@ant-design/icons";
import { Tooltip } from "../../design-system";
import { ReactComponent as ThinkerIcon } from "../../assets/icons/svg/thinker-gradient.svg";
import { AskChatPanel } from "./AskChatPanel";
import { useAssistantChat } from "./useAssistantChat";
import { useUserState } from "../../common/UserContext";
import { FeatureFlag, isFeatureFlagEnabled } from "../../types/UserGroup";
import type { ChatIntro } from "./types";
import classes from "./DrawerAssistant.module.less";

type AssistantMode = "ask" | "coach";

interface Props {
    open: boolean;
    onClose: () => void;
    projectId?: number;
    projectName?: string | null;
    /** Counterparty on the deal, as the vendor record names it. */
    vendorName?: string | null;
    analysisVersions?: number[];
    /** Serialized analysis output used to ground the assistant's answers. */
    analysisContent?: string;
}

export const DrawerAssistant: React.FC<Props> = ({
    open,
    onClose,
    projectId,
    projectName,
    vendorName,
    analysisVersions,
    analysisContent,
}) => {
    const { me } = useUserState();
    const [mode, setMode] = useState<AssistantMode>("ask");

    // The coach negotiates from the deal's leverage and targets, so it is only
    // offered once there is an analysis to argue from. The backend applies the
    // same rule when it decides whether the Q&A assistant may point at the
    // coach (AIAssistantService.resolveContext).
    const coachAvailable =
        isFeatureFlagEnabled(me, FeatureFlag.NEGOTIATION_COACH) && !!analysisContent;
    const activeMode: AssistantMode = coachAvailable ? mode : "ask";

    const subject = projectName ? `the report for "${projectName}"` : "this report";
    const counterparty = vendorName?.trim() || "the supplier";
    const pageDescription = `Viewing the analysis report${projectName ? ` for "${projectName}"` : ""}.`;

    // Each mode is a different system prompt, so each keeps its own
    // conversation: switching shows the other mode's transcript rather than
    // reinterpreting this one, and switching back picks up where it left off.
    // A reply still streaming in the hidden mode finishes there.
    const askChat = useAssistantChat({
        projectId,
        analysisVersions,
        analysisContent,
        pageName: "Project Analysis",
        pageDescription,
        mode: "ask",
    });
    const coachChat = useAssistantChat({
        projectId,
        analysisVersions,
        analysisContent,
        pageName: "Negotiation Coach",
        pageDescription,
        mode: "coach",
    });
    const { messages, isStreaming, error, send, stop, sendFeedback, reset } =
        activeMode === "coach" ? coachChat : askChat;

    // Shown while the active mode's conversation is empty, and again after a
    // clear. Clicking an example sends it as the first message.
    const intros = useMemo<Record<AssistantMode, ChatIntro>>(
        () => ({
            ask: {
                title: "Ask about this report",
                description: `I've read ${subject}. Ask me anything about it, or have me draft a message, and I'll work from what the analysis found.`,
                capabilities: [
                    "Answer questions about anything in the analysis",
                    "Explain pricing, terms, risks, savings and recommendations",
                    "Summarize the deal, or compare its options side by side",
                    "Draft negotiation emails and messages grounded in the analysis",
                ],
                examples: [
                    "Summarize this deal in three bullets",
                    "Where are the biggest savings opportunities?",
                    "Which contract terms carry the most risk?",
                    "Draft an email to open the negotiation",
                ],
            },
            coach: {
                title: "Negotiation coach",
                description: `Prepare for your negotiation with ${counterparty}. I argue from the leverage and targets in this analysis, and I'll tell you where your case is weak, not just where it's strong.`,
                capabilities: [
                    "Build your negotiation strategy and talking points",
                    "Work out what to say, and how to frame each ask",
                    `Anticipate ${counterparty}'s pushback, with responses ready`,
                    `Roleplay the negotiation with ${counterparty}, with coaching on every move`,
                    `Debrief: say "stop" at any point to see how close you got`,
                ],
                examples: [
                    "Build my negotiation strategy",
                    "How should I position my ask on price?",
                    `What pushback should I expect from ${counterparty}?`,
                    "Let's practice the negotiation",
                ],
            },
        }),
        [subject, counterparty]
    );
    const modeOptions = useMemo(
        () => [
            {
                value: "ask",
                label: "Assistant",
                description: "Answers questions about this report",
            },
            {
                value: "coach",
                label: "Negotiation coach",
                description: `Builds your strategy, roleplays ${counterparty} with live coaching, and debriefs you after`,
            },
        ],
        [counterparty]
    );

    return (
        <Drawer
            open={open}
            onClose={onClose}
            placement="right"
            width={520}
            mask={false}
            closable={false}
            destroyOnClose
            rootClassName={classes.drawerRoot}
            className={classes.drawer}
            bodyStyle={{ padding: 0 }}
        >
            <div className={classes.shell}>
                <div className={classes.header}>
                    <ThinkerIcon className={classes.headerIcon} />
                    <Tooltip title="Clear conversation" placement="bottom">
                        {/* Use aria-disabled, not the native `disabled` attribute:
                            antd's Tooltip strips the className off a disabled child
                            button (moving it to a wrapper span), which would drop our
                            styles and expose native control chrome. aria-disabled keeps
                            the class on the button and we guard the click ourselves. */}
                        <button
                            type="button"
                            className={classes.headerClear}
                            onClick={messages.length === 0 ? undefined : reset}
                            aria-disabled={messages.length === 0}
                            aria-label="Clear conversation"
                        >
                            <ClearOutlined />
                        </button>
                    </Tooltip>
                    <button
                        type="button"
                        className={classes.headerClose}
                        onClick={onClose}
                        title="Close"
                    >
                        <CloseOutlined />
                    </button>
                </div>

                <div className={classes.tabBody}>
                    <AskChatPanel
                        messages={messages}
                        isStreaming={isStreaming}
                        error={error}
                        onSend={send}
                        onStop={stop}
                        onFeedback={sendFeedback}
                        modes={coachAvailable ? modeOptions : undefined}
                        mode={activeMode}
                        onModeChange={(value) => setMode(value as AssistantMode)}
                        intro={intros[activeMode]}
                    />
                </div>
            </div>
        </Drawer>
    );
};
